import type { ReactNode } from 'react';
import { Group, Stack, Text, ThemeIcon } from '@mantine/core';
import type { Icon } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import type { BannerTone } from './bannerTones';

/**
 * MED-1116 — shared colored-callout shell for the two pre-session banners
 * (`AiBanner` + `PeerLeaderBanner`'s six states). A tinted ContentCard (matching
 * the AI listening banner from MED-1090 and the peer-prep context banner) with a
 * leading colored icon chip, a colored title, an optional subtitle, and a body
 * slot. These stay COLORED callouts (amber / green per state) — not neutral
 * cards — so the page's banners read as a distinct, attention-drawing treatment.
 *
 * Tones live in `bannerTones.ts` (BANNER_GREEN / BANNER_AMBER).
 */
interface BannerCalloutProps {
  tone: BannerTone;
  icon: Icon;
  title: string;
  subtitle?: string | null;
  children?: ReactNode;
}

export function BannerCallout({
  tone,
  icon: IconComponent,
  title,
  subtitle,
  children,
}: BannerCalloutProps) {
  return (
    <ContentCard
      variant="elevated"
      radius={16}
      padding="md"
      contentGap={0}
      fill={false}
      style={{ backgroundColor: tone.bg }}
    >
      {/* role="status" on the content group (ContentCard's root doesn't forward
          arbitrary ARIA) so the banner is announced. */}
      <Group role="status" wrap="nowrap" align="flex-start" gap="sm">
        <ThemeIcon
          size={36}
          radius="md"
          variant="light"
          color={tone.color}
          aria-hidden
        >
          <IconComponent size={20} />
        </ThemeIcon>
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <Stack gap={4}>
            <Text fw={700} fz="md" c={`${tone.color}.8`}>
              {title}
            </Text>
            {subtitle && (
              <Text size="sm" c="dimmed">
                {subtitle}
              </Text>
            )}
          </Stack>
          {children}
        </Stack>
      </Group>
    </ContentCard>
  );
}

export default BannerCallout;
